import gsap from 'gsap';
import { ScrollSmoother, ScrollTrigger } from 'gsap/all';
import { SplitText } from 'gsap/SplitText';
import { PageEvent } from './page-lifecycle/PageEvent';
import { forceFocusOnContentElement, isMobileDevice } from '../components/ComponentUtils';
import { Breakpoints } from '../components/Component';
import header from './header';

const SCROLL_DEBUG = false;
const SCROLL_POSITION = 'top 85%';
const SCROLL_POSITION_OBSERVER = '0px 0px -15% 0px';
const TOGGLE_ACTIONS_OBSERVER = 'none none none none'; // Ensure trigger is not fired automatically
const SCROLL_ANIMATION_OFFSET = 25;
const SCROLL_ANIMATION_DURATION = 0.4;

const TEXT_ANIMATION_OFFSET = 50;
const TEXT_ANIMATION_DURATION = 0.1;

const CHARS_ANIMATION_STAGGER = 0.01;
const WORDS_ANIMATION_STAGGER = 0.03;

interface ScrollControllerOptions {
	revealOnScroll: boolean;
}

const DEBUG_VERBOSE = false;
const LOG_PREFIX = `ScrollController`;

// ////////////////////////////////////////////////////////////////////

class ScrollController {
	private scrollSmootherPermitted: boolean = false;
	scrollSmoother!: ScrollSmoother;

	scrollTriggerObserveredElements = new Set<HTMLElement>();
	scrollTriggerObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;

				// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

				const el = entry.target as HTMLElement;

				const { scrolltriggerId } = el.dataset;

				if (!scrolltriggerId) return;

				// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
				// Find the related ScrollTrigger and play it

				const scrollTrigger = ScrollTrigger.getById(scrolltriggerId);

				if (scrollTrigger && scrollTrigger.animation) {
					scrollTrigger.animation.play().then(() => {
						// We only play each animation once so can kill after completion
						this.unobserveEl(el);
						scrollTrigger.kill();

						el.style.willChange = '';
					});
				}
			});
		},
		{ rootMargin: SCROLL_POSITION_OBSERVER, threshold: 0 }
	);

	constructor() {
		gsap.registerPlugin(ScrollTrigger);
		gsap.registerPlugin(SplitText);
		gsap.registerPlugin(ScrollSmoother);

		this.scrollSmootherPermitted = !isMobileDevice() && window.innerWidth > Breakpoints.sm;

		this.trySetSmoothingEnabled(true);
	}

	observeEl(el: HTMLElement) {
		this.scrollTriggerObserver.observe(el);
		this.scrollTriggerObserveredElements.add(el);
	}

	unobserveEl(el: HTMLElement) {
		this.scrollTriggerObserver.unobserve(el);
		this.scrollTriggerObserveredElements.delete(el);
	}

	trySetSmoothingEnabled(enabled: boolean) {
		if (!this.scrollSmootherPermitted) return;
		this.scrollSmoother?.kill();
		if (enabled) {
			this.scrollSmoother = ScrollSmoother.create({ smooth: 1, effects: false, smoothTouch: 0.2, ignoreMobileResize: true });
		}
	}

	init(options: ScrollControllerOptions): void {
		document.addEventListener(PageEvent.leave, () => {
			ScrollTrigger.getAll().forEach((trigger) => trigger.kill());
		});

		document.addEventListener(PageEvent.enter, (event: any) => {
			const newPageContainer = event.detail?.next?.container;
			this.processSamePageAnchors(newPageContainer);
		});

		if (options.revealOnScroll) {
			document.addEventListener(PageEvent.enterComplete, () => {
				this.setupElementAnimations();
			});

			document.addEventListener(PageEvent.leave, () => {
				this.purgeScrollTriggers();
			});
		}
	}

	private processSamePageAnchors(page: any): void {
		page = page || document;

		const links = page.querySelectorAll('a[href^="#"]') as NodeListOf<HTMLAnchorElement>;

		if (!links) return;

		// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -

		links.forEach((link) => {
			link.addEventListener('click', (event: Event) => {
				const element = event.target as HTMLLinkElement;
				if (element) {
					const href = element.getAttribute('href');
					if (href) {
						const target = document.querySelector(href) as HTMLElement;
						if (target) {
							const y = target.getBoundingClientRect().top + window.scrollY - (header.element?.clientHeight || 0) - 5;
							if (this.scrollSmootherPermitted) {
								this.scrollSmoother.scrollTo(y, true, 'top top');
							} else {
								window.scrollTo({ top: y, behavior: 'smooth' });
							}
						}
					}
					// console.log('Scrolling to: ', id);
					event.preventDefault();

					if (element.classList.contains('screen-reader-text')) {
						/**
						 * If the link is the a11y skip to link we want to force a focus on the content element.
						 * This does two things.
						 * 1: De-focus the "skip to" link which makes it disappear
						 * 2: Move the focus forward so any subsequent tabbing will start from after the menu
						 */

						const contentEl = document.getElementById('content');

						if (!contentEl) return console.warn('Inable to derive id "content"');

						forceFocusOnContentElement();
					}
				}
			});
		});
	}

	purgeScrollTriggers() {
		DEBUG_VERBOSE &&
			logger.log(
				LOG_PREFIX,
				this.purgeScrollTriggers.name,
				this.scrollTriggerObserveredElements,
				ScrollTrigger.getAll().length,
				this.scrollTriggerObserveredElements.size
			);

		// Iterate over the ScrollTrigger and kill all current triggers
		ScrollTrigger.getAll().forEach((trigger) => trigger.kill());

		// Ensure all scroll trigger elemets are unobserved
		this.scrollTriggerObserveredElements.forEach((el) => this.scrollTriggerObserver.unobserve(el));
		this.scrollTriggerObserveredElements.clear();

		DEBUG_VERBOSE &&
			logger.log(
				LOG_PREFIX,
				this.purgeScrollTriggers.name,
				this.scrollTriggerObserveredElements,
				ScrollTrigger.getAll().length,
				this.scrollTriggerObserveredElements.size
			);
	}

	setupElementAnimations() {
		DEBUG_VERBOSE && logger.log(LOG_PREFIX, this.setupElementAnimations.name);

		let elements: HTMLElement[] = Array.from(document.querySelectorAll('.animate')) as HTMLElement[];

		elements?.forEach((el) => {
			if (el.classList.contains('anim-chars')) {
				this.setupCharacterAnimation(el);
			} else if (el.classList.contains('anim-words')) {
				this.setupWordAnimation(el);
			} else {
				this.setupScrollAnimation(el);
			}
		});

		DEBUG_VERBOSE &&
			logger.log(LOG_PREFIX, this.setupElementAnimations.name, ScrollTrigger.getAll().length, this.scrollTriggerObserveredElements.size);
	}

	setupCharacterAnimation(el: HTMLElement) {
		const split = new SplitText(el, { type: 'chars,words' });

		gsap.from(split.chars, {
			scrollTrigger: {
				trigger: el,
				start: SCROLL_POSITION,
				markers: SCROLL_DEBUG,
				toggleActions: 'play none none none',
			},
			duration: TEXT_ANIMATION_DURATION,
			opacity: 0,
			yPercent: TEXT_ANIMATION_OFFSET,
			ease: 'back',
			stagger: CHARS_ANIMATION_STAGGER,
			onComplete: () => {
				split.revert();
			},
		});
	}

	setupWordAnimation(el: HTMLElement) {
		const split = new SplitText(el, { type: 'words' });
		gsap.from(split.words, {
			scrollTrigger: {
				trigger: el,
				start: SCROLL_POSITION,
				markers: SCROLL_DEBUG,
				toggleActions: 'play none none none',
			},

			duration: TEXT_ANIMATION_DURATION,
			opacity: 0,
			yPercent: TEXT_ANIMATION_OFFSET,
			ease: 'Circ.easeInOut',
			stagger: WORDS_ANIMATION_STAGGER,
			onComplete: () => {
				split.revert();
			},
		});
	}

	setupScrollAnimation(el: HTMLElement) {
		const delay = el.getAttribute('ros-delay') ? parseFloat(el.getAttribute('ros-delay') as string) : 0;

		const id = this.getScrollId();

		gsap.set(el, { y: `+=${SCROLL_ANIMATION_OFFSET}` });

		const tween = gsap.to(el, {
			opacity: 1,
			y: '0',
			duration: SCROLL_ANIMATION_DURATION,
			paused: true,
			delay,
		});

		ScrollTrigger.create({
			trigger: el,
			toggleActions: TOGGLE_ACTIONS_OBSERVER,
			animation: tween,
			id,
		});

		el.dataset.scrolltriggerId = id;

		el.style.willChange = 'transform, opacity';

		this.observeEl(el);
	}

	getScrollId() {
		return `scroll-${Math.random().toString(36).slice(2, 11)}`;
	}
}

export const scrollController = new ScrollController();
